import { Injectable, inject } from '@angular/core';
import { CoreApiService, RequestConfig } from '../core-api-service';
import { BehaviorSubject, combineLatest, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import {
  ClaimType,
  ExportHomeClaimPdfDTO,
  ExportMotorClaimPdfDTO,
  FileParamDTO,
  GetClaimRes,
  GetPolicyClaimsRes,
  GlobalNameDTO,
  HomeClaimCreateParamDTO,
  HomeClaimParticipantDTO,
  HomeParticipantDTO,
  MotorClaimCreateParamDTO,
  MotorClaimParticipantDTO,
  MotorParticipantDTO,
  NoteDTO,
  OpenClaimDTO,
  PhoneNumberDTO,
  PolicyListDTO,
  PolicyType
} from 'models/DTOs';
import { UserProfile } from 'app/services/user.service';
import { API_URLS } from '../../constants/core.constants';
import { ClaimsListDTO } from '../../mocks/mock';
import { NotificationService } from '@services/notification.service';
import {
  HomeClaimDTO,
  HomeClaimPages,
  HomeDamageType,
  MotorClaimDTO,
  MotorClaimPages,
  Participant,
  SelectPropertyLocation,
  SelectPropertyRisk,
  SelectVehicle
} from 'app/modules/claims/models/models';
import { PolicyService } from './policies.service';
import { formatDate } from '@angular/common';
import { EpicBridgeService } from './epic-bridge.service';

@Injectable({
  providedIn: 'root'
})
export class ClaimsService extends CoreApiService {
  private _notifications = inject(NotificationService);
  private _policyService: PolicyService = inject(PolicyService);
  private _epicBridgeService: EpicBridgeService = inject(EpicBridgeService);

  claimsSubject: BehaviorSubject<ClaimsListDTO[]> = new BehaviorSubject(null);

  createHealthClaim(claim: OpenClaimDTO | any): Observable<string> {
    console.log(claim);
    console.log('claim created successfully');
    return of('#JD23K4LS12312');
  }

  getAllClaims(user: UserProfile, ebPolicies: PolicyListDTO[]): Observable<ClaimsListDTO[]> {
    return combineLatest([this.getHealthClaims(user.memberID), this.getEBClaims(ebPolicies)]).pipe(
      map(result => {
        const healthClaims: ClaimsListDTO[] = result[0];
        const homeMotorClaims: ClaimsListDTO[] = result[1];
        const allClaims = [...healthClaims, ...homeMotorClaims];
        this.claimsSubject.next(allClaims);
        return allClaims;
      })
    );
  }

  getHealthClaims(memberID: string) {
    return of([] as ClaimsListDTO[]);
    // if (!memberID) return of([] as ClaimsListDTO[]);
    // return this.get<RootResOfListOfClaimDTO>(API_URLS.HTHClaims, { params: { memberId: memberID } }).pipe(
    //   map(response => {
    //     if (!response.success) {
    //       this._notifications.showMessage(`HTH: ${response.errorMessage}`);
    //       return [];
    //     }
    //     return response.data.map<ClaimsListDTO>(item => ({
    //       policyType: PolicyType.Health,
    //       policyTitle: 'Health Claim',
    //       claimID: item.claimNumber,
    //       status: item.status,
    //       accidentDate: item.dateOfIncident,
    //       risk: item.patientName
    //     }));
    //   })
    // );
  }

  getEBClaims(policies: PolicyListDTO[]) {
    return of(policies ?? []).pipe(
      take(1),
      map(items => items.filter(value => value.policyType !== PolicyType.Health)),
      switchMap(policies => {
        const claims: Observable<ClaimsListDTO[]>[] = [];
        policies.forEach(policy => {
          claims.push(
            this.get<GetPolicyClaimsRes>(API_URLS.ebClaims, { params: { policyID: policy.policyID } }).pipe(
              map(response => {
                if (!response.success) {
                  this._notifications.showMessage(`Epic bridge: ${response.errorMessage}`);
                  return [];
                }

                return response.claims.map<ClaimsListDTO>(item => ({
                  policyType: policy.policyType,
                  policyTitle: policy.policyTitle.replace('Policy', 'Claim'),
                  risk:
                    policy.policyType === PolicyType.Motor
                      ? `${item.claim.vehicleMake} ${item.claim.vehicleModel}`
                      : item.claim.riskDescription.split(' - SI:')[0],
                  claimID: item.claim.claimId,
                  claimNumber: item.claim.claimNumber,
                  status: item.claim.status,
                  amount: item.claim.sumInsured,
                  accidentDate: item.claim.lossDate,
                  claimDate: item.claim.lossDate,
                  incidentDescription: item.claim.description,
                  incidentLocation:
                    policy.policyType === PolicyType.Home
                      ? item.claim.riskDescription.split(' - SI:')[0]
                      : item.claim.location.streetName,
                  vehicleModel: item.claim.vehicleMake + ' ' + item.claim.vehicleModel
                }));
              })
            )
          );
        });

        return combineLatest([...claims]).pipe(
          map(list => {
            let claims: ClaimsListDTO[] = [];
            list.forEach(items => {
              claims = [...claims, ...items];
            });
            return claims;
          })
        );
      })
    );
  }

  getEBClaim(claimID: string) {
    return this.get<GetClaimRes>(`${API_URLS.epicbridge}/getclaim`, {
      params: { claimID: claimID }
    }).pipe(
      map(res => {
        if (!res.success) this._notifications.showMessage(res.errorMessage);
        return res.claim;
      })
    );
  }

  getVehicles(client: UserProfile): Observable<SelectVehicle[]> {
    const nationalIDTypeNumber = client.nationalIDTypeNumber;
    const nationalIDType = client.nationalIDType;

    return this._policyService.getEBPolicies({ nationalIDTypeNumber, nationalIDType }).pipe(
      switchMap(resp => {
        const filteredPolicies = resp.policies.filter(
          policy =>
            policy.policy.policyPrefix === 'PMC' ||
            policy.policy.policyPrefix === 'PMT' ||
            policy.policy.policyPrefix === 'PMTL'
        );

        const requests = filteredPolicies.map(policy =>
          this._policyService.getEBMotorPolicyRisks(policy.policy.policyId)
        );

        return forkJoin(requests).pipe(
          map(responses => {
            const vehicleList: SelectVehicle[] = [];

            responses.forEach(risks =>
              risks.risks.forEach(r => {
                const vehicle: SelectVehicle = {
                  isSelected: false,
                  name: r.make,
                  model: r.model,
                  registrationNumber: r.registrationNumber,
                  mortgage: r.mortgageeText,
                  policyId: risks.policyId,
                  colour: r.colour,
                  year: String(r.year),
                  mileage: String(r.mileage),
                  chassisNumber: r.chassisNumber
                };

                vehicleList.push(vehicle);
              })
            );

            return vehicleList;
          })
        );
      })
    );
  }

  getRiskLocations(client: UserProfile): Observable<SelectPropertyLocation[]> {
    const nationalIDTypeNumber = client.nationalIDTypeNumber;
    const nationalIDType = client.nationalIDType;

    return this._policyService.getEBPolicies({ nationalIDTypeNumber, nationalIDType }).pipe(
      switchMap(resp => {
        const filteredPolicies = resp.policies.filter(
          policy => policy.policy.policyPrefix === 'HCC' || policy.policy.policyPrefix === 'HCX'
        );

        const requests = filteredPolicies.map(policy =>
          this._policyService.getEBHomePolicyRisks(policy.policy.policyId)
        );

        return forkJoin(requests).pipe(
          map(responses => {
            const riskLocationsMap: { [location: string]: SelectPropertyLocation } = {};

            responses.forEach(risks => {
              risks.risks.forEach(r => {
                const address = `${r.address.buildingLabel} ${r.address.unitNumber}, ${r.address.island}, ${r.address.postalZipCode}, ${r.address.country}`;
                const risk: SelectPropertyRisk = {
                  isSelected: false,
                  location: address,
                  policyId: risks.policyId,
                  riskId: r.riskId,
                  description: r.riskDescription
                };

                if (!riskLocationsMap[address]) {
                  riskLocationsMap[address] = {
                    isSelected: false,
                    location: address,
                    risksList: [risk]
                  };
                } else {
                  riskLocationsMap[address].risksList.push(risk);
                }
              });
            });

            return Object.values(riskLocationsMap);
          })
        );
      })
    );
  }

  prepareFile(files: FileParamDTO[], name: string, content: string, description: string, docType: string) {
    const file: FileParamDTO = {
      name: name,
      content: content,
      description: description,
      docType: docType,
      fileType: name.split('.')[1],
      recordId: 'tmp_val',
      table: 'claim'
    };

    files.push(file);
  }

  submitHomeClaim(claimHome: HomeClaimDTO, data: any) {
    const currentDate = new Date().toLocaleDateString();
    const files: FileParamDTO[] = [];
    const participants: HomeClaimParticipantDTO[] = [];

    //Prepare files:
    data.damagePhotos.forEach(p => {
      this.prepareFile(files, p.name, p.content, 'Property damage photo', 'Photo of Insured Property');
    });
    const policeReports = claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT]?.data?.policeReportReceipt;
    if (policeReports && Array.isArray(policeReports)) {
      policeReports.forEach(p => {
        this.prepareFile(files, p.name, p.content, 'Police Report', 'Police Report');
      });
    }
    const dataFiles = claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO_REPAIRS]?.data?.files;
    if (dataFiles && Array.isArray(dataFiles)) {
      dataFiles.forEach(p => {
        this.prepareFile(files, p.name, p.content, 'Photos of repairs made prior to claim', 'Photograph');
      });
    }
    const repairsEstimate = claimHome.pageData[HomeClaimPages.DOCUMENTS]?.data?.repairsEstimate;
    if (repairsEstimate && Array.isArray(repairsEstimate)) {
      repairsEstimate.forEach(f => {
        this.prepareFile(files, f.name, f.content, 'Estimate of Repairs', 'Estimate of Repairs');
      });
    }
    const witnessStatement = claimHome.pageData[HomeClaimPages.DOCUMENTS]?.data?.witnessStatement;
    if (witnessStatement && Array.isArray(witnessStatement)) {
      witnessStatement.forEach(f => {
        this.prepareFile(files, f.name, f.content, 'Statement of Witness', 'Statement of Witness');
      });
    }

    //Prepare notes:
    const notes: Partial<NoteDTO>[] = [
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Weather at time: ' + (claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.weatherAtTime ?? 'N/A')
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Storm name: ' + (claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.stormName ?? 'N/A')
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Other insurances: ' +
          (claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.otherInsurances ? 'Yes' : 'No')
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Were premises occupied? ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.premisesOccupied ? 'Yes' : 'No') +
          ' Last date occupied: ' +
          formatDate(claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.lastDateOccupied, 'MM/dd/yyyy', 'en-US')
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Used as residential? ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.premisesUsed ? 'Yes' : 'No') +
          ' Description: ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.premisesUsedDescription ?? 'N/A')
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Alterations made after policy renewal? ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.materialAlterations ? 'Yes' : 'No') +
          ' Description: ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.materialAlterationsDescription ?? 'N/A')
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Have you previously suffered a similar loss? ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.similarLoss ? 'Yes' : 'No') +
          ' Description: ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.similarLossDescription ?? 'N/A')
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Any repairs made prior to claiming? ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO_REPAIRS].data.priorRepairs ? 'Yes' : 'No') +
          ' Description: ' +
          (claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO_REPAIRS].data.priorRepairsDescription ?? 'N/A')
      }
    ];

    //Prepare participants:
    const involvedParties = claimHome.pageData[HomeClaimPages.ADDITIONAL_PARTIES_INVOLVED]?.data?.involvedParties;
    if (witnessStatement && Array.isArray(witnessStatement)) {
      involvedParties.map(p => {
        const phoneNumber: Partial<PhoneNumberDTO> = {
          phoneNumber: p.contactNumber
        };

        const globalName: Partial<GlobalNameDTO> = {
          firstName: p.name.split(' ')[0],
          lastName: p.name.split(' ')[1] ?? '',
          dob: formatDate(p.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
          emailAddress: p.email,
          phoneNumbers: [phoneNumber as PhoneNumberDTO],
          isACompany: false,
          overrideExternallyFilled: false
        };

        const participant: Partial<HomeParticipantDTO> = {
          participantType: 'Third_Party',
          injuryDescription: p.injuryDetails,
          isDriverAlso: true,
          isThirdParty: true,
          locationIndex: 0,
          globalName: globalName as GlobalNameDTO
        };

        const motorParticipant: HomeClaimParticipantDTO = {
          participant: participant as HomeParticipantDTO,
          tempId: 0,
          linkedToTempId: 2
        };

        participants.push(motorParticipant);
      });
    }

    const homeClaim: Partial<HomeClaimCreateParamDTO> = {
      policyID: data.policy.policyId,
      policyPrefix: data.policy.policyPrefix,
      claimType: ClaimType.HomeClaim,
      lossCause: claimHome.pageData[HomeClaimPages.DAMAGE_TYPES].data.selectedDamageType,
      reportExist: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.reportExist,
      officerName: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.officerName,
      officerNo: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.officerNo,
      reportNumber: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.reportNumber,
      premisesOccupied: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.premisesOccupied,
      lastDateOccupied: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.lastDateOccupied,
      premisesUsed: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.premisesUsed,
      premisesUsedDescription: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.premisesUsedDescription,
      materialAlterations: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.materialAlterations,
      materialAlterationsDescription:
        claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.materialAlterationsDescription,
      similarLoss: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.similarLoss,
      similarLossDescription: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.similarLossDescription,
      weatherAtTime: String(claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT]?.data?.weatherAtTime || ''),
      stormName: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.stormName,
      otherInsurances: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.otherInsurances,
      priorRepairs: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO_REPAIRS].data.priorRepairs,
      dateDocumentsReceived: currentDate, //Not in UI
      dateReportedToInsurer: currentDate, //Mandatory and not in UI
      description: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.description,
      email: data.email,
      notificationMethod: 'eMail',
      dateOfLoss: formatDate(
        claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.dateOfLoss,
        'MM/dd/yyyy',
        'en-US'
      ),
      timeOfLoss: String(claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT]?.data?.timeOfLoss || ''),
      insurerName: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.insuredName,
      riskIds: data.riskIds,
      participants: participants,
      notes: notes as NoteDTO[],
      files: files
    };

    return this.post(API_URLS.ebSubmitHomeClaim, homeClaim as HomeClaimCreateParamDTO);
  }

  submitMotorClaim(claimMotor: MotorClaimDTO, data: any) {
    const currentDate = new Date().toLocaleDateString();
    const participants: MotorClaimParticipantDTO[] = [];
    const files: FileParamDTO[] = [];

    //Prepare files:
    data.incidentPhotos.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Vehicle damage photo', 'Photo of Insured Vehicle');
    });
    claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.driversLicence.forEach(f => {
      this.prepareFile(files, f.name, f.content, "Vehicle driver's license", "Driver's Licence");
    });
    claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.incidentFootage?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Incident footage photo', 'Photograph');
    });
    claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.files?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Repair invoice', 'Invoice');
    });
    claimMotor.pageData[MotorClaimPages.ADDITIONAL_PARTIES_INVOLVED].data.license?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Additional parties license', "Driver's Licence");
    });
    claimMotor.pageData[MotorClaimPages.ADDITIONAL_PARTIES_INVOLVED].data.certificate?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Additional parties certificate', 'Certificate');
    });
    claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.estimateOfRepairs?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Estimate of Repairs', 'Estimate of Repairs');
    });
    claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.mortgageReleaseLetter?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Mortgagee Release Letter', 'Mortgagee Letter');
    });
    claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.policeReportReceipt?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Police Report', 'Police Report');
    });
    claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.witnessStatement?.forEach(f => {
      this.prepareFile(files, f.name, f.content, 'Statement of Witness', 'Statement of Witness');
    });

    //Prepare notes:
    const notes: Partial<NoteDTO>[] = [
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Consent: ' + claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.consent
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Conviction History: ' + claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.convictionHistory
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Who was at fault: ' + claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.whoIsFault
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Vehicle being used for: ' + claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.vehicleBeingUsed
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Weather: ' +
          (claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.weather.some(x => x === 'Other')
            ? claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.weatherOther
            : String(claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.weather))
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Road condition: ' +
          (claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.roadCondition.some(x => x === 'Other')
            ? claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.roadConditionOther
            : String(claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.roadCondition))
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Lights used: ' + claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.lightsUsed
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Traveling speed: ' + claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.speed
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Number of passengers: ' + claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.passangers
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Mileage: ' + claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.mileage
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Trailer used: ' + claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.trailerUsed
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Trailer length: ' + claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.trailerLength
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Trailer weight: ' + claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.trailerWeight
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Description of goods carried: ' + claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.goodsDescription
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Prefered repairer description: ' +
          claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.preferredRepairerDescription
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes:
          'Current location of the vehicle: ' +
          claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.vehicleLocation
      },
      {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Mortgage name: ' + claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.mortgage
      }
    ];
    claimMotor.pageData[MotorClaimPages.ADDITIONAL_PARTIES_INVOLVED].data.involvedParties?.forEach(ip => {
      const note1: Partial<NoteDTO> = {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Name: ' + ip.name + ' Paying passengers: ' + ip.payingPassangers
      };
      const note2: Partial<NoteDTO> = {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Name: ' + ip.name + ' Any property or buildings damaged: ' + ip.wasPropertyDamaged
      };
      const note3: Partial<NoteDTO> = {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Name: ' + ip.name + ' Property location: ' + ip.propertyLocation
      };
      const note4: Partial<NoteDTO> = {
        type: 'Assessment Of Damages - Prep notes',
        notes: 'Name: ' + ip.name + ' Damage details: ' + ip.damageDetails
      };

      notes.push(note1);
      notes.push(note2);
      notes.push(note3);
      notes.push(note4);
    });

    //Prepare participants:
    claimMotor.pageData[MotorClaimPages.ADDITIONAL_PARTIES_INVOLVED].data.involvedParties.map(p => {
      const phoneNumbers: Partial<PhoneNumberDTO>[] = [
        {
          phoneNumber: p.contactNumber
        }
      ];

      const globalName: Partial<GlobalNameDTO> = {
        firstName: p.name.split(' ')[0],
        lastName: p.name.split(' ')[1] ?? '',
        dob: formatDate(p.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
        emailAddress: p.email,
        phoneNumbers: phoneNumbers as PhoneNumberDTO[],
        isACompany: false,
        overrideExternallyFilled: false
      };

      const participant: Partial<MotorParticipantDTO> = {
        participantType: 'Third_Party',
        injuryDescription: p.injuryDetails,
        isDriverAlso: true,
        isThirdParty: true,
        locationIndex: 0,
        globalName: globalName as GlobalNameDTO
      };

      const motorParticipant: MotorClaimParticipantDTO = {
        participant: participant as MotorParticipantDTO,
        temp_id: 0,
        linkedToTempId: 2
      };

      participants.push(motorParticipant);
    });
    claimMotor.pageData[MotorClaimPages.ADDITIONAL_PARTIES_INVOLVED].data.witnesses.map(p => {
      const participant: Partial<MotorParticipantDTO> = {
        participantType: 'Witness',
        injuryDescription: p.injuryDetails,
        isDriverAlso: false,
        isThirdParty: true,
        locationIndex: 0
      };

      const motorParticipant: MotorClaimParticipantDTO = {
        participant: participant as MotorParticipantDTO,
        temp_id: 0,
        linkedToTempId: 2
      };

      participants.push(motorParticipant);
    });

    const motorClaim: Partial<MotorClaimCreateParamDTO> = {
      policyPrefix: data.policy.policyPrefix,
      policyID: data.policy.policyId,
      claimType: ClaimType.MotorClaim,
      lossCause: data.damageType,
      driverName: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.name,
      insurerName: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.name,
      dateDocumentsReceived: currentDate,
      dateReportedToInsurer: currentDate,
      description: claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.description,
      email: data.email,
      notificationMethod: 'eMail',
      policeStationId: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.policeStationId,
      dateOfLoss: formatDate(
        claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.dateOfLoss,
        'MM/dd/yyyy',
        'en-US'
      ),
      timeOfLoss: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.timeOfLoss,
      selectedVehicle: claimMotor.pageData[MotorClaimPages.VEHICLE].data.selectedfVehicle,
      officerName: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.officerName,
      officerNo: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.officerNo,
      latitude: data.location.latitude,
      longitude: data.location.longitude,
      streetName: data.location.location,
      riskIds: data.riskIds,
      participants: participants,
      notes: notes as NoteDTO[],
      files: files
    };

    return this.post(API_URLS.ebSubmitMotorClaim, motorClaim as MotorClaimCreateParamDTO);
  }

  createPdfMotorClaim(claimMotor: MotorClaimDTO, data: any) {
    let involvedParties = [];
    involvedParties = claimMotor.pageData[MotorClaimPages.ADDITIONAL_PARTIES_INVOLVED].data.involvedParties?.map(
      item => {
        const parties: any = {
          contactNumber: item.contactNumber,
          damageDetails: item.damageDetails,
          dateOfBirth: formatDate(item.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
          email: item.email,
          injuryDetails: item.injuryDetails,
          insuranceNumber: item.insuranceNumber,
          insuranceProvider: item.insuranceProvider,
          name: item.name,
          payingPassangers: item.payingPassangers,
          propertyLocation: item.propertyLocation,
          vehicleLicenseNumber: item.vehicleLicenseNumber,
          wasPropertyDamaged: item.wasPropertyDamaged,
          wasThirdPartyInjured: item.wasThirdPartyInjured
        };

        return parties;
      }
    );

    var witnesses = [];
    witnesses = claimMotor.pageData[MotorClaimPages.ADDITIONAL_PARTIES_INVOLVED].data.witnesses?.map(item => {
      const witness: any = {
        address: item.address,
        contactNumber: item.contactNumber,
        dateOfBirth: formatDate(item.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
        email: item.email,
        injuryDetails: item.injuryDetails,
        name: item.name,
        wasWitnessInjured: item.wasWitnessInjured
      };

      return witness;
    });
    let isRight: string = 'No';
    let isTop: string = 'No';
    let isBottom: string = 'No';
    let isLeft: string = 'No';
    let incidentPhotos = [];
    incidentPhotos = claimMotor.pageData[MotorClaimPages.INCIDENT_PHOTOS].data.incidentPhotos?.map(item => {
      if (item.position === 0) isTop = 'Yes';
      if (item.position === 1) isRight = 'Yes';
      if (item.position === 2) isBottom = 'Yes';
      if (item.position === 3) isLeft = 'Yes';
    });

    var lights = (claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.lightsUsed as []).join('');
    var roadCondition = (claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.roadCondition as []).join('');
    var weather = (claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.weather as []).join('');

    const motorClaim: ExportMotorClaimPdfDTO = {
      claimRef: '',
      companyName: data.clientName,
      dob: formatDate(data.clientDateOfBirth, 'MM/dd/yyyy', 'en-US'),
      nationality: data.clientNacionality,
      cellNo: data.clientPhone,
      otherNo: data.clientPhone,
      occupation: data.clientOccupation,
      emailAdress: data.clientEmail,
      address: data.clientAddress,
      registrationNo: data.registrationNo,
      makeModel: data.model,
      manufactureYear: data.yearVehicle,
      mileage: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.mileage, //data.mileageVehicle,
      isYourEmployee: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.relationship ? 'Yes' : 'No',
      expiryDate: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.expiryDate
        ? formatDate(claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.expiryDate, 'MM/dd/yyyy', 'en-US')
        : '',
      issueDate: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.issueDate
        ? formatDate(claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.issueDate, 'MM/dd/yyyy', 'en-US')
        : '',
      licenseNo: String(claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.licenseNo),
      driverDob: formatDate(claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
      driverNationality: '',
      driverCellNo: '',
      driverOtherNo: '',
      location: claimMotor.pageData[MotorClaimPages.LOCATION].data.location,
      dateOfLoss: formatDate(
        claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.dateOfLoss,
        'MM/dd/yyyy',
        'en-US'
      ),
      timeOfLoss: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.timeOfLoss,
      isTravellingDirection: claimMotor.pageData[MotorClaimPages.LOCATION].data.travelDirection,
      howManyPassengers: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.passangers,
      /**/
      isLightsUsed: lights,
      roadOther: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data?.roadConditionOther,
      isRoad: roadCondition,
      weatherOther: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data?.weatherOther,
      isWeather: weather,
      isKMPH: 'KMPH',
      isThereFootage: claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data?.hasImages ? 'Yes' : 'No',
      isSpeed: 'Other',
      speedOther: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data?.speed,
      statement: claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data?.description,
      estDamageCost: '',
      isRight: isRight,
      rightDamage: '',
      isFront: '',
      frontDamage: '',
      isLeft: isLeft,
      leftDamage: '',
      isRear: isBottom,
      rearDamage: '',
      isRoof: isTop,
      roofDamage: '',
      /**/
      explanatorySketch: '',
      otherPolicies: '',
      giveParticulars: '',
      vehicleUsedFor: claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.vehicleBeingUsed,
      ownerSignature: data.userSignature,
      ownerTitle: data.genderType == 'Male' ? 'MR' : 'MS',
      driverTitle: '',
      driverName: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.name,
      isDrivingWithConsent: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.consent,
      photosOfIncident: '',
      driverLicense: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.driversLicence ? 'Yes' : 'No',
      ownerDriverLicense: '',

      estimateOfRepairs: claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.estimateOfRepairs ? 'Yes' : 'No',
      vehicleLogbook: '',
      completedIncidentForm: '',
      companyName2: '',
      dob2: '',
      nationality2: '',
      cellNo2: '',
      otherNo2: '',
      occupation2: '',
      emailAdress2: '',
      ownerTitle2: '',
      driverOccupation: '',
      detailsOfAnAccidents: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.accidentHistory,
      detailsOfAnyConvictions: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.convictionHistory,
      relationship: claimMotor.pageData[MotorClaimPages.DRIVER_INFO].data.relationship,

      isTypeOfLoss: claimMotor.pageData[MotorClaimPages.DAMAGE_TYPES].data.selectedDamageType,

      vinChassisNo: data.chassisNumberVehicle,
      weightOfLoad: claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.trailerWeight,

      isMortgaged: claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.hasMortgage ? 'Yes' : 'No',
      stateMortgagee: claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.mortgage,
      anotherInsuranceOnTheVehicle: '',
      isPassengersCharged: '',
      vehicleBeingUsed: claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.vehicleBeingUsed,
      isTiresSuspension: '',
      tiresSuspensionDamage: '',
      totalLossDamage: '',
      isDriverInjured: '',
      injuryDetails: '',
      injuryDetails2: '',
      otherPersons1Name: '',
      otherPersons1Address: '',
      otherPersons1Address2: '',
      otherPersons1DriverName: '',
      otherPersons1ContactNo: '',
      otherPersons1VehicleLicense: '',
      otherPersons1VehicleDamages: '',
      otherPersons1PropertyDamage: '',
      otherPersons1PropertyAddress: '',
      otherPersons1PropertyAddress2: '',
      otherPersons1InsurerPolicy: '',
      otherPersons1PersonInjured: '',
      otherPersons1DateOfBirth: '',
      otherPersons1InjuryDetails: '',
      otherPersons1InjuryDetails2: '',
      otherPersons2Name: '',
      otherPersons2Address: '',
      otherPersons2Address2: '',
      otherPersons2DriverName: '',
      otherPersons2ContactNo: '',
      otherPersons2VehicleLicense: '',
      otherPersons2VehicleDamages: '',
      otherPersons2PropertyDamage: '',
      otherPersons2PropertyAddress: '',
      otherPersons2PropertyAddress2: '',
      otherPersons2InsurerPolicy: '',
      otherPersons2PersonInjured: '',
      otherPersons2DateOfBirth: '',
      otherPersons2InjuryDetails: '',
      otherPersons2InjuryDetails2: '',
      ispersonMadeAStatement: '',
      anyPersonMadeAStatement: '',
      yourOpinion: claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.whoIsFault,
      addressOfAnyIndependentWitnesses3: '',
      addressOfAnyIndependentWitnesses4: '',
      ownerSignature2: '',
      mortgageReleaseLetter: claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.mortgageReleaseLetter ? 'Yes' : 'No',
      witnessStatement: claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.witnessStatement ? 'Yes' : 'No',
      photosOrFootageOfIncident: claimMotor.pageData[MotorClaimPages.INCIDENT_INFO].data.hasImages ? 'Yes' : 'No',
      isMonthlyIncome: '',
      isTrailerAttached: claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.trailerUsed ? 'Yes' : 'No',
      isGoodsCarried: '',
      insurer: '',
      typeOfCover: '',
      policyNo: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.officerNo,
      periodOfCover: '',
      policyExcess: '',
      colour: data.colourVehicle,
      reportedPoliceStation: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.policeStation,
      officerName: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.officerName,
      badgeNo: claimMotor.pageData[MotorClaimPages.ACCIDENT_DETAILS].data.officerNo,
      policyNo2: '',
      model2: '',
      make2: '',
      cinicoClientTitle: '',
      cinicoClientCellNo: '',
      cinicoClientAddress: '',
      cinicoClientCompanyName: '',
      driverName2: '',
      driverAddress: '',
      isTotalLoss: '',
      preferredRepairer: claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.preferredRepairerDescription,
      underCarriageDamage: '',
      isUnderCarriage: '',
      currentLocation: claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.vehicleLocation,
      propertyDamageDetails: '',
      hospital: '',
      addressOfAnyIndependentWitnesses2: '',
      addressOfAnyIndependentWitnesses1: '',
      vehicleRegistrationCertificate: '',
      insurersCoverLetter: '',
      receiptOfPoliceReport: claimMotor.pageData[MotorClaimPages.DOCUMENTS].data.policeReportReceipt ? 'Yes' : 'No',
      completedIncidentFormSubmitted: '',
      proofOfIdentification: '',
      estimate: claimMotor.pageData[MotorClaimPages.ADDITIONAL_INFO].data.repairCost,
      deedCurrentUtility: '',
      originalMedicalBills: '',
      recentPaySlipProofOfIncome: '',
      proofOfIdentificationInjuryClaim: '',
      medicalReportInjuryClaim: '',
      completedIncidentFormSubmittedInjuryClaim: '',
      damageType: claimMotor.pageData[MotorClaimPages.DAMAGE_TYPES].data.selectedDamageType,
      memberID: data.memberID
    };

    if (involvedParties.length > 0) {
      (motorClaim.otherPersons1Address = ''),
        (motorClaim.otherPersons1ContactNo = involvedParties[0].contactNumber),
        (motorClaim.otherPersons1PropertyDamage = involvedParties[0].damageDetails),
        (motorClaim.otherPersons1DateOfBirth = formatDate(involvedParties[0].dateOfBirth, 'MM/dd/yyyy', 'en-US')),
        (motorClaim.otherPersons1InjuryDetails = involvedParties[0].injuryDetails),
        (motorClaim.otherPersons1InsurerPolicy = involvedParties[0].insuranceNumber),
        (motorClaim.otherPersons1InjuryDetails2 = involvedParties[0].insuranceProvider),
        (motorClaim.otherPersons1Name = involvedParties[0].name),
        (motorClaim.otherPersons1PropertyAddress = involvedParties[0].propertyLocation),
        (motorClaim.otherPersons1VehicleLicense = involvedParties[0].vehicleLicenseNumber),
        (motorClaim.otherPersons1PropertyDamage = involvedParties[0].wasPropertyDamaged ? 'Yes' : 'No'),
        (motorClaim.otherPersons1PersonInjured = involvedParties[0].wasThirdPartyInjured ? 'Yes' : 'No');
    }

    if (involvedParties.length > 1) {
      (motorClaim.otherPersons2Address = ''),
        (motorClaim.otherPersons2ContactNo = involvedParties[1].contactNumber),
        (motorClaim.otherPersons2PropertyDamage = involvedParties[1].damageDetails),
        (motorClaim.otherPersons2DateOfBirth = formatDate(involvedParties[1].dateOfBirth, 'MM/dd/yyyy', 'en-US')),
        (motorClaim.otherPersons2InjuryDetails = involvedParties[1].injuryDetails),
        (motorClaim.otherPersons2InsurerPolicy = involvedParties[1].insuranceNumber),
        (motorClaim.otherPersons2InjuryDetails2 = involvedParties[1].insuranceProvider),
        (motorClaim.otherPersons2Name = involvedParties[1].name),
        (motorClaim.otherPersons2PropertyAddress = involvedParties[1].propertyLocation),
        (motorClaim.otherPersons2VehicleLicense = involvedParties[1].vehicleLicenseNumber),
        (motorClaim.otherPersons2PropertyDamage = involvedParties[1].wasPropertyDamaged ? 'Yes' : 'No'),
        (motorClaim.otherPersons2PersonInjured = involvedParties[1].wasThirdPartyInjured ? 'Yes' : 'No');
    }
    //witnesses
    if (witnesses.length > 0) {
      (motorClaim.addressOfAnyIndependentWitnesses1 = witnesses[0].name + ', ' + witnesses[0].address),
        (motorClaim.addressOfAnyIndependentWitnesses2 = witnesses[0].injuryDetails);
    }

    if (witnesses.length > 1) {
      (motorClaim.addressOfAnyIndependentWitnesses1 = witnesses[1].name + ', ' + witnesses[0].address),
        (motorClaim.addressOfAnyIndependentWitnesses2 = witnesses[1].injuryDetails);
    }

    const requestConfig: RequestConfig = {
      responseType: 'blob'
    };

    return this.post(API_URLS.exportMotorClaim, motorClaim, requestConfig);
  }

  createPdfHomeClaim(claimHome: HomeClaimDTO, data: any) {
    var personsList: Participant[] = [];
    var weather = claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.weatherAtTime
      ? (claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data?.weatherAtTime as []).join('')
      : '';
    var witnesses = [];
    witnesses = claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.witnesses?.map(item => {
      const witness: any = {
        name: item.witnessName,
        phoneNumber: item.phoneNumber
      };

      return witness;
    });

    var dataInvolved = claimHome?.pageData[HomeClaimPages.ADDITIONAL_PARTIES_INVOLVED]?.data;
    if (dataInvolved?.additionalParties) {
      if (dataInvolved?.additionalParties) {
        personsList = dataInvolved?.additionalParties;
      }
      if (
        claimHome.pageData[HomeClaimPages.DAMAGE_TYPES].data?.selectedDamageType === HomeDamageType.Other &&
        dataInvolved?.additionalClaimantParticulars
      ) {
        personsList = dataInvolved?.additionalClaimantParticulars;
      }
    }
    //

    const homeClaim: ExportHomeClaimPdfDTO = {
      claimRef: '',
      proposer1: data.genderType == 'Male' ? 'MR' : 'MS',
      proposerFullName1: data.clientName,
      dob1: formatDate(data.clientDateOfBirth, 'MM/dd/yyyy', 'en-US'),
      nationality1: data.clientNacionality,
      cellNo1: data.clientPhone,
      otherNo1: '',
      occupation1: data.clientOccupation,
      emailAddress1: data.clientEmail,
      mailingAddress1: data.clientAddress,
      proposer2: '',
      proposerFullName2: '',
      dob2: '',
      nationality2: '',
      cellNo2: '',
      otherNo2: '',
      occupation2: '',
      emailAddress2: '',
      mailingAddress2: '',
      policyNo: String(data.policyNo),
      periodOfCoverFrom: data.periodOfCoverFrom ? data?.periodOfCoverFrom.substring(0, 10) : '',
      periodOfCoverTo: data.periodOfCoverTo ? data?.periodOfCoverTo.substring(0, 10) : '',
      mailingAddress: '',
      typeOfLossOther: '',
      typeOfLoss1: claimHome.pageData[HomeClaimPages.DAMAGE_TYPES].data?.selectedDamageType,
      riskDateReported: '',
      riskAddress: claimHome.pageData[HomeClaimPages.RISK_LOCATION].data?.riskDescription,
      dateOfLoss: formatDate(
        claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data?.dateOfLoss,
        'MM/dd/yyyy',
        'en-US'
      ),
      timeOfLoss: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data?.timeOfLoss,
      isThereFootage: claimHome.pageData[HomeClaimPages.DAMAGED_PICTURES].data?.damagedPhotos ? 'Yes' : 'No',
      isThereFootage1: 'Yes',
      weather1: weather,
      weatherOther1: '',
      isResidential: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data?.premisesUsed ? 'Yes' : 'No',
      residentialDetails: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data?.premisesUsedDescription,
      policeStation: '',
      officerName: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data?.officerName,
      badgeNo: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.officerNo,
      isPoliceReportMade: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.reportExist ? 'Yes' : 'No',
      isPolicyRenewal: '',
      policyRenewalDetails: '',
      lastOccupied: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data?.lastDateOccupied
        ? formatDate(claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data?.lastDateOccupied, 'MM/dd/yyyy', 'en-US')
        : '',
      lenderHoldingInterest: '',
      anyOtherInsurances: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.insuredName,
      priorToReporting: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO_REPAIRS].data?.priorRepairsDescription,
      previouslySuffered: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data.similarLossDescription,
      nameOfTheStorm: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.stormName,
      statement: claimHome.pageData[HomeClaimPages.EXPLAIN_INCIDENT].data.description,
      /**/
      vehicleRegistrationNo: personsList.length > 0 ? personsList[0]?.vehicleRegistration : '',
      makeModel: personsList.length > 0 ? personsList[0]?.vehicleMake : '',
      insuranceCompany: '', //personsList.length > 0? personsList[0]?.insuranceProvider : '',
      ownersName: personsList.length > 0 ? personsList[0]?.ownerName : '',
      driversName: personsList.length > 0 ? personsList[0]?.driversName : '',
      insurancePolicyNo: personsList.length > 0 ? personsList[0]?.vehicleRegistration : '',
      /**/
      claimant1: data.clientName,
      claimant1DateOfBirth: formatDate(data.clientDateOfBirth, 'MM/dd/yyyy', 'en-US'),
      claimant1ContactNo: data.clientPhone,
      claimant1Address: data.clientAddress,
      claimant1Employer: '',
      claimant1Occupation: data.clientOccupation,
      claimant1PurposeOfVisit: '',
      claimant1NatureOfInjury: '',
      claimant1IsYourEmployee: data.employeeID,
      claimant1Relationship: '',
      claimant1MaritalStatus: '',
      /* * */
      claimant2: '',
      claimant2DateOfBirth: '',
      claimant2ContactNo: '',
      claimant2Address: '',
      claimant2Employer: '',
      claimant2Occupation: '',
      claimant2PurposeOfVisit: '',
      claimant2NatureOfInjury: '',
      claimant2IsYourEmployee: '',
      claimant2Relationship: '',
      claimant2MaritalStatus: '',
      /**/
      receivedNotice: '',
      isReceivedNotice: '',
      isYourOpinion: '',
      yourOpinion: '',
      witnesses1: '',
      witnesses2: '',
      witnesses3: '',
      witnesses4: '',
      signatureUrl1: data.userSignature,
      signatureUrl2: '',
      estimateOfRepairs: '',
      photosOrFootage: '',
      mortgageReleaseLetter: '',
      deedCurrentUtilityBill: '',
      proofOfIdentification: '',
      policeReportReceipt: '',
      /* * */
      itemNo1: '',
      decription1: '',
      doP1: '',
      voL1: '',
      crC1: '',
      /* * */
      totalAmountBeingClaimed: String(claimHome.pageData[HomeClaimPages.ESTIMATED_DAMAGES].data?.buildingClaimedAmount),
      yearBuilt: String(claimHome.pageData[HomeClaimPages.ESTIMATED_DAMAGES].data?.buildingYearBuilt),
      totalSquareFeet: String(claimHome.pageData[HomeClaimPages.ESTIMATED_DAMAGES].data?.buildingTotalSquare),
      totalAmountSalvage: String(claimHome.pageData[HomeClaimPages.ESTIMATED_DAMAGES].data?.contentClaimedAmount),
      totalNoOfItems: String(claimHome.pageData[HomeClaimPages.ESTIMATED_DAMAGES].data?.contentTotalItems),
      stormDamage: '',
      isMaterialAlterations: claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data?.materialAlterations
        ? 'Yes'
        : 'No',
      materialAlterationsDescription:
        claimHome.pageData[HomeClaimPages.ADDITIONAL_INFO].data?.materialAlterationsDescription
    };

    homeClaim.estimateOfRepairs = claimHome.pageData[claimHome.pageData.length - 1].data.repairsEstimate ? 'Yes' : 'No';

    //witnesses
    if (witnesses.length > 0) {
      (homeClaim.witnesses1 = witnesses[0].name), (homeClaim.witnesses2 = witnesses[0].phoneNumber);
    }

    if (witnesses.length > 1) {
      (homeClaim.witnesses3 = witnesses[1].name), (homeClaim.witnesses4 = witnesses[1].phoneNumber);
    }

    console.log(homeClaim);

    const requestConfig: RequestConfig = {
      responseType: 'blob'
    };
    return this.post(API_URLS.exportHomeClaim, homeClaim, requestConfig);
  }
}
